
function makeValidation(fn, defaultMsg) {
  fn.withMessage = msg => {
    return makeValidation(v => fn(v), msg)
  }
  fn.required = () => {
    return groupValidations([required, fn])
  }
  fn.message =  defaultMsg
  return fn
}

const FAILED = Symbol('FAILED')

export const runValidations = validations => value => {
  validations = Array.isArray(validations) ? validations : [validations]
  const failed = validations.find(v => v(value) === FAILED)
  if (failed) {
    return failed.message
  }
}

export const groupValidations = (validations, message) => {
  const resetMessage = !message
  const fn = value => {
    const firstFail = validations.find(v => v(value) === FAILED)
    if (firstFail) {
      if (resetMessage) {
        message = firstFail.message
      }
      return FAILED
    }
  }
  fn.withMessage = msg => {
    return makeValidation(v => fn(v), msg)
  }
  fn.required = () => {
    return groupValidations([required, fn])
  }
  Object.defineProperty(fn, 'message', {
    get() {
      return message || 'Not Valid'
    }
  })
  return fn
}

export const required = makeValidation(value => {
  if (!value) {
    return FAILED
  }
}, 'Required')

export const email = makeValidation(value => {
  if (value && !value.match(/^[^@]+@[^.]+\..+$/)) {
    return FAILED
  }
}, 'Must be a valid email')

export const phone = makeValidation(value => {
  if (value && !value.match(/^[2-9]\d{9}$/)) {
    return FAILED
  }
}, 'Must be a valid phone number')

export const ssn = makeValidation(value => {
  if (!value)  {
    return false
  }
  if (!value.match(/^\d{9}$/)) {
    return FAILED // Must be exactly 9 digits
  }
  if (value.match(/^9/)) {
    return FAILED // Cannot start with 9
  }
  if (value.match(/^666/)) {
    return FAILED // Cannot start with 666
  }
  if (value.match(/^000/)) {
    return FAILED // Cannot start with 000
  }
  if (value.match(/^\d{3}00/)) {
    return FAILED // Cannot have 00 for middle section
  }
  if (value.match(/0000$/)) {
    return FAILED // Cannot end with 0000
  }
}, 'Please enter a valid Social Security number')

export const photo = makeValidation(value => {
  if (value && !value.dataUrl) {
    return FAILED
  }
}, 'Photo Required')

export const integer = makeValidation(value => {
  if (value && !value.match(/^\d+$/)) {
    return FAILED
  }
}, 'Must be an integer')

export const maxLength = count => makeValidation(value => {
  if (value && value.length > count) {
    return FAILED
  }
}, `Maximum ${count} characters required`)

export const minLength = count => makeValidation(value => {
  if (value && value.length < count) {
    return FAILED
  }
}, `Minimum ${count} characters required`)

export const exactLength = length => groupValidations(
  [minLength(length), maxLength(length)],
  `Exactly ${length} characters required`
)

export const betweenLength = (a, b) => groupValidations(
  [minLength(a), maxLength(b)],
  `Must be between ${a} and ${b} characters`
)

export const doesNotContainDob = makeValidation(value => {
  if (value && value.match(/\d{1,2} ?\/ ?\d{1,2} ?\/ ?\d{2}/)) {
    return FAILED
  }
}, 'Please do not enter any personal information, such as birthdays or names, into this field')

export const not = other => makeValidation(value => {
  if (other === value) {
    return FAILED
  }
}, `Cannot Be ${other}`)

export const exactly = other => makeValidation(value => {
  if (other !== value) {
    return FAILED
  }
}, `Must be exactly "${other}"`)

export const lte = number => makeValidation(value => {
  if (value && Number(value.replace(/[^\d.]/g, '')) > number) {
    return FAILED
  }
}, `Cannot exceed ${number}`)

export const gte = number => makeValidation(value => {
  if (value && Number(value.replace(/[^\d.]/g, '')) < number) {
    return FAILED
  }
}, `Must be at least ${number}`)

export const decimal = makeValidation(value => {
  if (value && value.split('.').length > 2) {
    return FAILED
  }
}, 'Only 1 decimal place allowed')

export const date = makeValidation(value => {
  if (value && !value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return FAILED
  }
}, 'Must be MM DD YYYY')

export const monthYear = makeValidation(value => {
  if (value && !value.match(/^\d{2}\/\d{4}$/)) {
    return FAILED
  }
}, 'Must be MM YYYY')

export const validMonthYear = makeValidation(value => {
  if (value && value.match(/^\d{2}\/\d{4}$/)) {
    const [month, year] = value.split('/')
    if (Number(month) < 0 || Number(month) > 12) {
      return FAILED
    }
  }
}, 'Date is invalid')

export const validDate = makeValidation(value => {
  if (value && value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const date = new Date(value)
    if (isNaN(date.getTime())) {
      return FAILED
    }
  }
}, 'Date is invalid')

export const after = date => makeValidation(value => {
  if (!value) {
    return
  }
  if (value.match(/^\d{2}\/\d{4}$/)) {
    const [month, year] = value.split('/')
    value = `${month}/01/${year}`
  }
  if(new Date(value) <= new Date(date)) {
    return FAILED
  }
}, `Must be after ${new Date(date).toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}`)

export const legalAdult = makeValidation(value => {
  const yearsAgo18 = new Date().setFullYear(new Date().getFullYear()-18)
  const comparing = new Date(value).getTime()
  if (comparing > yearsAgo18) {
    return FAILED
  }
}, `Must be 18 years or older`)

export const stillLiving = makeValidation(value => {
  const yearsAgo100 = new Date().setFullYear(new Date().getFullYear()-125)
  const comparing = new Date(value).getTime()
  if (comparing < yearsAgo100) {
    return FAILED
  }
}, `Must be alive`)